/**
 * WORKFLOW NODE TYPE SCHEMAS - Runtime Implementation
 *
 * Constantes e funções para o sistema de schemas de nodes de workflow.
 * Usado para validação de contexto disponível e opções condicionais.
 *
 * NOTA: Os tipos (interfaces) estão em @troia-v3/types.
 * Este arquivo contém apenas as constantes e funções runtime.
 */

import type {
  WorkflowNodeType,
  AvailableContext,
  NodeTypeSchema,
  ConditionalOption,
} from '@troia-v3/types';

import { entityForEventType } from './event-entities';

// Re-export types for convenience
export type { AvailableContext, NodeTypeSchema, ConditionalOption };

/**
 * Chaves de ENTIDADE booleanas do AvailableContext (as que representam
 * entidades disponíveis no contexto). Exclui campos auxiliares como
 * `instagramAuthor` (flag) e `contextContactLabel` (legenda), que não são
 * entidades e não devem ser iteradas como "context dots" nem em useHasContext.
 */
export type ContextEntityKey = 'conversation' | 'contact' | 'lead' | 'ticket' | 'ai' | 'event';

// ============================================================
// SCHEMAS DOS TRIGGERS
// ============================================================

/**
 * Trigger: Webhook
 * Recebe dados externos via HTTP - não fornece contexto de entidades
 */
export const TRIGGER_WEBHOOK_SCHEMA: NodeTypeSchema = {
  type: 'trigger_webhook',
  category: 'triggers',
  label: 'Webhook',
  description: 'Dispara quando um webhook é recebido',
  input: {
    requiresContext: {}
  },
  output: {
    providesContext: {
      conversation: false,
      contact: false,
      lead: false,
      ticket: false
    }
  }
};

/**
 * Trigger: Agendamento (Cron)
 * Execução periódica - não fornece contexto de entidades
 */
export const TRIGGER_SCHEDULE_SCHEMA: NodeTypeSchema = {
  type: 'trigger_schedule',
  category: 'triggers',
  label: 'Agendamento',
  description: 'Dispara em horários programados (cron)',
  input: {
    requiresContext: {}
  },
  output: {
    providesContext: {
      conversation: false,
      contact: false,
      lead: false,
      ticket: false
    }
  }
};

/**
 * Trigger: Evento de Mensagem Recebida
 * Fornece a conversa, o contato dela e o lead do contato (`relatedContextFor`)
 */
export const TRIGGER_EVENT_MESSAGE_RECEIVED_SCHEMA: NodeTypeSchema = {
  type: 'trigger_event',
  category: 'triggers',
  label: 'Mensagem Recebida',
  description: 'Dispara quando uma mensagem é recebida',
  input: {
    requiresContext: {}
  },
  output: {
    providesContext: triggerProvidesContext('conversation')
  }
};

/**
 * Trigger: Evento de Conversa Criada
 * Fornece a conversa, o contato dela e o lead do contato (`relatedContextFor`)
 */
export const TRIGGER_EVENT_CONVERSATION_CREATED_SCHEMA: NodeTypeSchema = {
  type: 'trigger_event',
  category: 'triggers',
  label: 'Conversa Criada',
  description: 'Dispara quando uma nova conversa é iniciada',
  input: {
    requiresContext: {}
  },
  output: {
    providesContext: triggerProvidesContext('conversation')
  }
};

/**
 * Trigger: Evento de Ticket Criado
 * Fornece o ticket, o contato dele, a conversa (a de onde o ticket nasceu se
 * estiver aberta, senão a aberta do contato) e o lead do contato
 * (`relatedContextFor`). Ticket sem conversa aberta chega sem conversa.
 */
export const TRIGGER_EVENT_TICKET_CREATED_SCHEMA: NodeTypeSchema = {
  type: 'trigger_event',
  category: 'triggers',
  label: 'Ticket Criado',
  description: 'Dispara quando um novo ticket é criado',
  input: {
    requiresContext: {}
  },
  output: {
    providesContext: triggerProvidesContext('ticket')
  }
};

/**
 * Trigger: Evento de Lead Criado
 * Fornece o lead, o contato dele (todo lead tem contactId) e a conversa ABERTA
 * do contato (`relatedContextFor`) — sem conversa aberta, chega sem conversa.
 */
export const TRIGGER_EVENT_LEAD_CREATED_SCHEMA: NodeTypeSchema = {
  type: 'trigger_event',
  category: 'triggers',
  label: 'Lead Criado',
  description: 'Dispara quando um novo lead é criado',
  input: {
    requiresContext: {}
  },
  output: {
    providesContext: triggerProvidesContext('lead')
  }
};

/**
 * Trigger: Evento de Contato Criado
 * Fornece o contato, a conversa ABERTA dele e o lead dele (`relatedContextFor`)
 */
export const TRIGGER_EVENT_CONTACT_CREATED_SCHEMA: NodeTypeSchema = {
  type: 'trigger_event',
  category: 'triggers',
  label: 'Contato Criado',
  description: 'Dispara quando um novo contato é criado',
  input: {
    requiresContext: {}
  },
  output: {
    providesContext: triggerProvidesContext('contact')
  }
};

/**
 * Trigger: Manual
 * Execução manual pelo usuário - não fornece contexto inicial
 */
export const TRIGGER_MANUAL_SCHEMA: NodeTypeSchema = {
  type: 'trigger_manual',
  category: 'triggers',
  label: 'Manual',
  description: 'Execução manual iniciada pelo usuário',
  input: {
    requiresContext: {}
  },
  output: {
    providesContext: {
      conversation: false,
      contact: false,
      lead: false,
      ticket: false
    }
  }
};

/**
 * Trigger: Campo de Data
 * Depende da entidade configurada
 */
export const TRIGGER_DATE_FIELD_SCHEMA: NodeTypeSchema = {
  type: 'trigger_date_field',
  category: 'triggers',
  label: 'Campo de Data',
  description: 'Dispara baseado em datas de campos (aniversário, vencimento, etc.)',
  input: {
    requiresContext: {}
  },
  output: {
    // Fallback estático (usado se entityType não estiver no config).
    // calculateContextAfterNode resolve dinamicamente via ENTITY_TYPE_CONTEXT.
    providesContext: {
      conversation: false,
      contact: false,
      lead: false,
      ticket: false
    }
  }
};

/**
 * Trigger: Inatividade
 * Depende da entidade monitorada
 */
export const TRIGGER_INACTIVITY_SCHEMA: NodeTypeSchema = {
  type: 'trigger_inactivity',
  category: 'triggers',
  label: 'Inatividade',
  description: 'Dispara quando uma entidade fica inativa por um período',
  input: {
    requiresContext: {}
  },
  output: {
    // Fallback estático (usado se entityType não estiver no config).
    // calculateContextAfterNode resolve dinamicamente via ENTITY_TYPE_CONTEXT
    // quando entityType está presente.
    providesContext: {
      conversation: false,
      contact: false,
      lead: false,
      ticket: false
    }
  }
};

// ============================================================
// SCHEMAS DAS ACTIONS
// ============================================================

/**
 * Action: Enviar Mensagem
 * Opções condicionais baseadas em contexto disponível
 */
export const ACTION_SEND_MESSAGE_SCHEMA: NodeTypeSchema = {
  type: 'action_send_message',
  category: 'actions',
  label: 'Enviar Mensagem',
  description: 'Envia texto, imagem, áudio, vídeo ou documento',
  input: {
    optionalContext: {
      conversation: true,
      contact: true,
    },
  },
  output: {
    providesContext: {
      conversation: true,
      contact: false,
      lead: false,
      ticket: false,
    },
  },
  conditionalOptions: [
    {
      field: 'targetType',
      value: 'current',
      label: 'Conversa do trigger',
      description: 'Envia na conversa que disparou o workflow',
      requiresContext: { conversation: true },
    },
    {
      field: 'targetType',
      value: 'new_conversation',
      label: 'Nova conversa',
      description: 'Cria uma nova conversa com canal e contato selecionados',
      requiresContext: {},
    },
  ],
};

/**
 * Action: Enviar Email
 */
export const ACTION_SEND_EMAIL_SCHEMA: NodeTypeSchema = {
  type: 'action_send_email',
  category: 'actions',
  label: 'Enviar Email',
  description: 'Envia um email usando template ou conteúdo personalizado',
  input: {
    optionalContext: {
      contact: true  // Para usar email do contato
    }
  },
  output: {
    providesContext: {
      conversation: false,
      contact: false,
      lead: false,
      ticket: false
    }
  },
  conditionalOptions: [
    {
      field: 'recipientType',
      value: 'contact_email',
      label: 'Email do Contato',
      description: 'Usa o email do contato no contexto',
      requiresContext: { contact: true }
    },
    {
      field: 'recipientType',
      value: 'custom_email',
      label: 'Email Personalizado',
      description: 'Informe um email específico',
      requiresContext: {}
    }
  ]
};

/**
 * Action: Requisição HTTP
 */
export const ACTION_HTTP_REQUEST_SCHEMA: NodeTypeSchema = {
  type: 'action_http_request',
  category: 'actions',
  label: 'Requisição HTTP',
  description: 'Faz uma requisição HTTP para uma API externa',
  input: {
    requiresContext: {}  // Não requer contexto específico
  },
  output: {
    providesContext: {
      conversation: false,
      contact: false,
      lead: false,
      ticket: false
    }
  }
};

/** Ação: Clonar Voz (efêmero — skill de música) */
export const ACTION_VOICE_CLONE_SCHEMA: NodeTypeSchema = {
  type: 'action_voice_clone',
  category: 'actions',
  label: 'Clonar Voz',
  description: 'Clona a voz de uma URL de áudio e gera amostra expressiva (efêmero)',
  input: { requiresContext: {} },
  output: { providesContext: { conversation: false, contact: false, lead: false, ticket: false } }
};

/** Ação: Falar com Voz Clonada */
export const ACTION_VOICE_TTS_SCHEMA: NodeTypeSchema = {
  type: 'action_voice_tts',
  category: 'actions',
  label: 'Falar com Voz Clonada',
  description: 'TTS de um texto com um clone efêmero existente → mp3 no S3',
  input: { requiresContext: {} },
  output: { providesContext: { conversation: false, contact: false, lead: false, ticket: false } }
};

/** Ação: Deletar Voz Clonada */
export const ACTION_VOICE_CLONE_DELETE_SCHEMA: NodeTypeSchema = {
  type: 'action_voice_clone_delete',
  category: 'actions',
  label: 'Deletar Voz Clonada',
  description: 'Deleta o clone efêmero (libera o slot do provider)',
  input: { requiresContext: {} },
  output: { providesContext: { conversation: false, contact: false, lead: false, ticket: false } }
};

/** Ação: Espelhar Mídia no S3 */
export const ACTION_MIRROR_MEDIA_SCHEMA: NodeTypeSchema = {
  type: 'action_mirror_media',
  category: 'actions',
  label: 'Espelhar Mídia',
  description: 'Re-hospeda uma URL externa no S3 (com corte opcional de prévia)',
  input: { requiresContext: {} },
  output: { providesContext: { conversation: false, contact: false, lead: false, ticket: false } }
};

/** Controle: Tentar Novamente (subgrafo retentável) */
export const CONTROL_RETRY_SCOPE_SCHEMA: NodeTypeSchema = {
  type: 'control_retry_scope',
  category: 'controls',
  label: 'Tentar Novamente',
  description: 'Repete o trecho conectado em "Tentar" até dar certo ou esgotar as tentativas',
  input: { requiresContext: {} },
  output: { providesContext: { conversation: false, contact: false, lead: false, ticket: false } }
};

/**
 * Action: Consultar Banco de Dados
 */
export const ACTION_QUERY_DATABASE_SCHEMA: NodeTypeSchema = {
  type: 'action_query_database',
  category: 'actions',
  label: 'Consultar Banco',
  description: 'Executa uma consulta no banco de dados',
  input: {
    requiresContext: {}
  },
  output: {
    providesContext: {
      conversation: false,
      contact: false,
      lead: false,
      ticket: false
    }
  }
};

/**
 * Action: Criar Lead
 * IMPORTANTE: Lead sempre tem contactId, então fornece BOTH lead AND contact
 */
export const ACTION_CREATE_LEAD_SCHEMA: NodeTypeSchema = {
  type: 'action_create_lead',
  category: 'actions',
  label: 'Criar Lead',
  description: 'Cria um novo lead associado a um contato',
  input: {
    // Funciona melhor com contato já disponível
    optionalContext: {
      contact: true
    }
  },
  output: {
    // Lead criado sempre tem contactId associado
    // Portanto fornece AMBOS: lead E contact
    providesContext: {
      conversation: false,
      contact: true,   // Lead sempre referencia um contact
      lead: true,
      ticket: false
    }
  },
  conditionalOptions: [
    {
      field: 'contactSource',
      value: 'context_contact',
      label: 'Contato do Contexto',
      description: 'Usa o contato disponível no workflow',
      requiresContext: { contact: true }
    },
    {
      field: 'contactSource',
      value: 'create_new',
      label: 'Criar Novo Contato',
      description: 'Cria um novo contato junto com o lead',
      requiresContext: {}
    },
    {
      field: 'contactSource',
      value: 'select_existing',
      label: 'Selecionar Contato',
      description: 'Selecione um contato existente',
      requiresContext: {}
    }
  ]
};

/**
 * Action: Atualizar Contato
 */
export const ACTION_UPDATE_CONTACT_SCHEMA: NodeTypeSchema = {
  type: 'action_update_contact',
  category: 'actions',
  label: 'Atualizar Contato',
  description: 'Atualiza dados, tags ou campos customizados do contato',
  input: {
    optionalContext: {
      contact: true
    }
  },
  output: {
    // Mantém o contact no contexto (atualizado)
    providesContext: {
      conversation: false,
      contact: true,
      lead: false,
      ticket: false
    }
  },
  conditionalOptions: [
    {
      field: 'contactSource',
      value: 'context_contact',
      label: 'Contato do Contexto',
      description: 'Atualiza o contato disponível no workflow',
      requiresContext: { contact: true }
    },
    {
      field: 'contactSource',
      value: 'select_contact',
      label: 'Selecionar Contato',
      description: 'Selecione um contato específico',
      requiresContext: {}
    }
  ]
};

/**
 * Action: Atribuir
 * Atribui recursos (ticket, conversa, lead) a usuários/equipes
 */
export const ACTION_ASSIGN_SCHEMA: NodeTypeSchema = {
  type: 'action_assign',
  category: 'actions',
  label: 'Atribuir',
  description: 'Atribui um recurso a um usuário ou equipe',
  input: {
    optionalContext: {
      conversation: true,
      ticket: true,
      lead: true
    }
  },
  output: {
    providesContext: {
      conversation: false,
      contact: false,
      lead: false,
      ticket: false
    }
  },
  conditionalOptions: [
    {
      field: 'resourceType',
      value: 'conversation',
      label: 'Conversa Atual',
      description: 'Atribui a conversa do contexto',
      requiresContext: { conversation: true }
    },
    {
      field: 'resourceType',
      value: 'ticket',
      label: 'Ticket Atual',
      description: 'Atribui o ticket do contexto',
      requiresContext: { ticket: true }
    },
    {
      field: 'resourceType',
      value: 'lead',
      label: 'Lead Atual',
      description: 'Atribui o lead do contexto',
      requiresContext: { lead: true }
    }
  ]
};

/**
 * Action: Definir Variável
 */
export const ACTION_SET_VARIABLE_SCHEMA: NodeTypeSchema = {
  type: 'action_set_variable',
  category: 'actions',
  label: 'Definir Variável',
  description: 'Define uma variável para uso em nodes posteriores',
  input: {
    requiresContext: {}
  },
  output: {
    providesContext: {
      conversation: false,
      contact: false,
      lead: false,
      ticket: false
    }
  }
};

/**
 * Action: Criar Conversa
 * Cria uma nova conversa com um contato
 * Requer contexto de contato para criar a conversa
 */
export const ACTION_CREATE_CONVERSATION_SCHEMA: NodeTypeSchema = {
  type: 'action_create_conversation',
  category: 'actions',
  label: 'Criar Conversa',
  description: 'Cria uma nova conversa com um contato',
  // optionalContext: o CreateConversationConfig permite escolher "Contato específico"
  // e o backend usa config.contactId (com fallback pra lead.contactId) — não bloquear
  // o Save de fluxos sem contato no contexto (ex: agendamento → criar conversa).
  input: {
    optionalContext: {
      contact: true
    }
  },
  output: {
    providesContext: {
      conversation: true,
      contact: false,
      lead: false,
      ticket: false
    }
  }
};

// ============================================================
// SCHEMAS DOS CONTROLS
// ============================================================

/**
 * Control: Condição IF
 * Não altera contexto - apenas direciona o fluxo
 */
export const CONTROL_IF_SCHEMA: NodeTypeSchema = {
  type: 'control_if',
  category: 'controls',
  label: 'Condição IF',
  description: 'Direciona o fluxo baseado em uma condição',
  input: {
    requiresContext: {}
  },
  output: {
    // Passa o contexto adiante sem alteração
    providesContext: {
      conversation: false,
      contact: false,
      lead: false,
      ticket: false
    }
  }
};

/**
 * Control: Switch/Case
 */
export const CONTROL_SWITCH_SCHEMA: NodeTypeSchema = {
  type: 'control_switch',
  category: 'controls',
  label: 'Switch',
  description: 'Direciona para diferentes caminhos baseado em um valor',
  input: {
    requiresContext: {}
  },
  output: {
    providesContext: {
      conversation: false,
      contact: false,
      lead: false,
      ticket: false
    }
  }
};

/**
 * Control: Loop
 */
export const CONTROL_LOOP_SCHEMA: NodeTypeSchema = {
  type: 'control_loop',
  category: 'controls',
  label: 'Loop',
  description: 'Repete um conjunto de ações',
  input: {
    requiresContext: {}
  },
  output: {
    providesContext: {
      conversation: false,
      contact: false,
      lead: false,
      ticket: false
    }
  }
};

// ============================================================
// SCHEMAS DOS NODES DE IA
// ============================================================

/**
 * AI: Agente de IA
 */
export const AI_AGENT_SCHEMA: NodeTypeSchema = {
  type: 'ai_agent',
  category: 'ai',
  label: 'Agente de IA',
  description: 'Executa um agente de IA para processar a conversa',
  // O agente roda numa conversa, mas o backend AIAgentStepFactory RESOLVE a
  // conversa a partir de contextType/contextId (contato/lead → conversa mais
  // recente) quando o gatilho não traz conversa. Por isso é optionalContext, não
  // requiresContext — senão o validador de design-time bloqueia o Save de fluxos
  // válidos (lead.won → agente). O AIAgentConfig expõe essa origem e o runtime
  // valida o que faltar.
  input: {
    optionalContext: {
      conversation: true,
      contact: true,
      lead: true
    }
  },
  output: {
    providesContext: {
      conversation: false,
      contact: false,
      lead: false,
      ticket: false
    }
  }
};

/**
 * AI: Agente IA Inline
 * Agente Mastra customizado dentro do workflow. Define prompt/model próprios
 * e recebe tools via handle "tools" (action nodes viram ferramentas do LLM).
 * Fornece variáveis {{ai.response}}, {{ai.toolsCalled}} para nodes posteriores.
 */
export const AI_AGENT_INLINE_SCHEMA: NodeTypeSchema = {
  type: 'ai_agent_inline',
  category: 'ai',
  label: 'Agente IA Inline',
  description: 'Agente customizado com prompt próprio e tools conectadas',
  input: {
    optionalContext: {
      conversation: true,
      contact: true,
      lead: true
    }
  },
  output: {
    providesContext: {
      conversation: false,
      contact: false,
      lead: false,
      ticket: false,
      ai: true  // Fornece variáveis de AI para nodes posteriores
    }
  }
};

// ============================================================
// SKILL NODES
// ============================================================

/**
 * Skill Input: ponto de entrada de um workflow usado como skill.
 * Substitui o trigger e carrega os metadados do skill.
 */
export const SKILL_INPUT_SCHEMA: NodeTypeSchema = {
  type: 'skill_input',
  category: 'triggers',
  label: 'Entrada do Skill',
  description: 'Ponto de entrada invocado pelo agente (substitui o trigger em skills)',
  input: {
    requiresContext: {}
  },
  output: {
    providesContext: {
      conversation: false,
      contact: false,
      lead: false,
      ticket: false
    }
  }
};

/**
 * Skill Output: ponto de saída de um workflow usado como skill.
 * Mapeia variáveis do contexto para o payload de retorno.
 */
export const SKILL_OUTPUT_SCHEMA: NodeTypeSchema = {
  type: 'skill_output',
  category: 'actions',
  label: 'Saída do Skill',
  description: 'Retorna o resultado do skill ao agente',
  input: {
    requiresContext: {}
  },
  output: {
    providesContext: {
      conversation: false,
      contact: false,
      lead: false,
      ticket: false
    }
  }
};

// ============================================================
// REGISTRO DE TODOS OS SCHEMAS
// ============================================================

/**
 * Mapa de todos os schemas de nodes por tipo
 */
// makeMinimalSchema: usar SOMENTE para node types recém-adicionados em
// @troia-v3/types que ainda não têm UI. Todos os types com preset/config DEVEM
// ter schema real (requires/providesContext) declarado abaixo.
// Exportado para permanecer disponível a tipos futuros sem violar noUnusedLocals.
export const makeMinimalSchema = (
  type: WorkflowNodeType,
  category: NodeTypeSchema['category'],
  label: string,
): NodeTypeSchema => ({
  type,
  category,
  label,
  description: '',
  input: { optionalContext: {} },
  output: {
    providesContext: {
      conversation: false,
      contact: false,
      lead: false,
      ticket: false,
    },
  },
  conditionalOptions: [],
});

// ============================================================
// SCHEMAS REAIS DOS NODE TYPES COM UI (antes placeholders)
// Requisitos de contexto espelham o que os StepFactories exigem em runtime.
// ============================================================

const noProvides = { conversation: false, contact: false, lead: false, ticket: false };

/**
 * Trigger: Comentário no Instagram
 * Fornece contact quando o comentador já é um contato (identifier instagram);
 * o Entity Enrichment popula inputData.contact no runtime nesse caso.
 * NÃO fornece conversation: um comentário público não cria conversa.
 */
const TRIGGER_INSTAGRAM_COMMENT_SCHEMA: NodeTypeSchema = {
  type: 'trigger_instagram_comment', category: 'triggers', label: 'Comentário no Instagram',
  description: 'Dispara quando um comentário é feito em post/reel da conta conectada',
  input: { requiresContext: {} },
  output: { providesContext: { ...noProvides, contact: true, instagramAuthor: true } },
};

/**
 * Trigger: Menção no Instagram
 * Fornece contact quando o mentionador já é um contato (identifier instagram);
 * o Entity Enrichment popula inputData.contact no runtime nesse caso.
 * NÃO fornece conversation: uma menção pública não cria conversa.
 */
const TRIGGER_INSTAGRAM_MENTION_SCHEMA: NodeTypeSchema = {
  type: 'trigger_instagram_mention', category: 'triggers', label: 'Menção no Instagram',
  description: 'Dispara quando a conta conectada é marcada (@) em legenda ou comentário',
  input: { requiresContext: {} },
  output: { providesContext: { ...noProvides, contact: true, instagramAuthor: true } },
};

const ACTION_SEND_TEMPLATE_SCHEMA: NodeTypeSchema = {
  type: 'action_send_template', category: 'actions', label: 'Enviar Template',
  description: 'Envia template WhatsApp aprovado ao contato',
  // optionalContext (não requiresContext): o SendTemplateConfig permite escolher
  // "Contato específico" e o backend usa config.contactId — não bloquear o Save
  // de fluxos sem contato no contexto (ex: agendamento → template).
  input: { optionalContext: { contact: true } },
  output: { providesContext: noProvides },
};

const ACTION_SEND_MEDIA_SCHEMA: NodeTypeSchema = {
  type: 'action_send_media', category: 'actions', label: 'Enviar Mídia',
  description: 'Envia mídia na conversa atual',
  input: { requiresContext: { conversation: true } },
  output: { providesContext: noProvides },
};

const ACTION_UPDATE_LEAD_SCHEMA: NodeTypeSchema = {
  type: 'action_update_lead', category: 'actions', label: 'Atualizar Lead',
  description: 'Atualiza campos do lead do contexto',
  // optionalContext: o UpdateLeadConfig permite leadSource='specific' (config.leadId)
  // e o backend resolve por ele — não bloquear o Save quando não há lead no contexto.
  input: { optionalContext: { lead: true } },
  output: { providesContext: noProvides },
};

const ACTION_ADD_TAG_SCHEMA: NodeTypeSchema = {
  type: 'action_add_tag', category: 'actions', label: 'Adicionar Tag',
  description: 'Adiciona tags ao contato',
  input: { requiresContext: { contact: true } },
  output: { providesContext: noProvides },
};

const ACTION_REMOVE_TAG_SCHEMA: NodeTypeSchema = {
  type: 'action_remove_tag', category: 'actions', label: 'Remover Tag',
  description: 'Remove tags do contato',
  input: { requiresContext: { contact: true } },
  output: { providesContext: noProvides },
};

const ACTION_CREATE_TICKET_SCHEMA: NodeTypeSchema = {
  type: 'action_create_ticket', category: 'actions', label: 'Criar Ticket',
  description: 'Cria ticket no helpdesk',
  input: { optionalContext: { contact: true, conversation: true } },
  output: { providesContext: { ...noProvides, ticket: true } },
};

const ACTION_INTERNAL_NOTIFICATION_SCHEMA: NodeTypeSchema = {
  type: 'action_internal_notification', category: 'actions', label: 'Notificação Interna',
  description: 'Notifica usuário ou equipe',
  input: { requiresContext: {} },
  output: { providesContext: noProvides },
};

const ACTION_FIND_LEADS_SCHEMA: NodeTypeSchema = {
  type: 'action_find_leads', category: 'actions', label: 'Buscar Leads',
  description: 'Busca leads por filtros para iteração',
  input: { requiresContext: {} },
  output: { providesContext: noProvides },
};

const ACTION_CREATE_DATABASE_DOCUMENT_SCHEMA: NodeTypeSchema = {
  type: 'action_create_database_document', category: 'actions', label: 'Criar Documento em Database',
  description: 'Cria documento em base de conhecimento',
  input: { requiresContext: {} },
  output: { providesContext: noProvides },
};

const ACTION_CREATE_CHECKLIST_SCHEMA: NodeTypeSchema = {
  type: 'action_create_checklist', category: 'actions', label: 'Criar Checklist',
  description: 'Cria checklist(s) a partir de um modelo, em unidades sorteadas ou fixas',
  // O node NÃO depende de contexto: os dois alvos são por unidade. (O alvo
  // 'entidade do contexto' foi removido em 14/08/2026 — ver CreateChecklistConfig.)
  input: {},
  output: { providesContext: noProvides },
};

const ACTION_FIND_UNIT_SCHEMA: NodeTypeSchema = {
  type: 'action_find_unit', category: 'actions', label: 'Buscar unidade',
  description: 'Busca uma unidade por CNPJ/CPF e grava o resultado em uma variável',
  input: {},
  output: { providesContext: noProvides },
};

const ACTION_FIND_USER_SCHEMA: NodeTypeSchema = {
  type: 'action_find_user', category: 'actions', label: 'Buscar usuário',
  description: 'Busca um usuário por documento, código ou email e grava o resultado em uma variável',
  input: {},
  output: { providesContext: noProvides },
};

const ACTION_FIND_CONTACT_SCHEMA: NodeTypeSchema = {
  type: 'action_find_contact', category: 'actions', label: 'Buscar ou criar contato',
  description: 'Busca um contato por telefone ou email, podendo criar se não existir',
  input: {},
  output: { providesContext: noProvides },
};

const ACTION_URL_TO_PDF_SCHEMA: NodeTypeSchema = {
  type: 'action_url_to_pdf', category: 'actions', label: 'Gerar PDF de uma página',
  description: 'Salva uma página web como PDF, hospeda o arquivo e grava o link em uma variável',
  input: {},
  output: { providesContext: noProvides },
};

/**
 * Action: Transferir Conversa (2026-09-23)
 * A conversa vem do contexto (gatilho, ou "Criar conversa" logo antes) ou de
 * uma variável do fluxo — por isso `optionalContext`: sem conversa no contexto
 * o painel oferece só "Variável do fluxo", sem bloquear o Salvar.
 */
const ACTION_TRANSFER_CONVERSATION_SCHEMA: NodeTypeSchema = {
  type: 'action_transfer_conversation', category: 'actions', label: 'Transferir Conversa',
  description: 'Transfere a conversa para um usuário ou uma equipe',
  input: { optionalContext: { conversation: true } },
  output: { providesContext: noProvides },
};

/**
 * Action: CSAT — Satisfação do Cliente (2026-09-23)
 * Mesma conversa-alvo do Transferir conversa (contexto ou variável), por isso
 * também `optionalContext`.
 */
const ACTION_SATISFACTION_SURVEY_SCHEMA: NodeTypeSchema = {
  type: 'action_satisfaction_survey', category: 'actions', label: 'CSAT — Satisfação do Cliente',
  description: 'Encerra a conversa e envia a pesquisa de satisfação do canal',
  input: { optionalContext: { conversation: true } },
  output: { providesContext: noProvides },
};

/**
 * Action: Pergunta (2026-09-28)
 * Pergunta na conversa-alvo (contexto ou variável — mesmo contrato do CSAT) e
 * espera a resposta. A resposta vai para `variables[saveAs]`, não para o contexto.
 */
const ACTION_ASK_QUESTION_SCHEMA: NodeTypeSchema = {
  type: 'action_ask_question', category: 'actions', label: 'Pergunta',
  description: 'Envia uma pergunta, espera a resposta do cliente e confere se ela serve',
  input: { optionalContext: { conversation: true } },
  output: { providesContext: noProvides },
};

/**
 * Action: Perguntar formulário (2026-09-28) — mesmo contrato de conversa da
 * Pergunta; as respostas vão para `variables[saveAs]`, não para o contexto.
 */
const ACTION_ASK_FORM_SCHEMA: NodeTypeSchema = {
  type: 'action_ask_form', category: 'actions', label: 'Perguntar formulário',
  description: 'Faz as perguntas de um formulário, uma por uma, e confere cada resposta',
  input: { optionalContext: { conversation: true } },
  output: { providesContext: noProvides },
};

/**
 * Action: Número sequencial (2026-09-28). No modo "Gerar cupom do lead"
 * (2026-09-29) usa o lead do fluxo ou o de uma variável — por isso
 * `optionalContext` (molde do Salvar formulário).
 */
const ACTION_NEXT_NUMBER_SCHEMA: NodeTypeSchema = {
  type: 'action_next_number', category: 'actions', label: 'Número sequencial',
  description: 'Entrega o próximo número de uma sequência com nome (ex.: número da sorte) ou o cupom de sorteio do lead',
  input: { optionalContext: { lead: true } },
  output: { providesContext: noProvides },
};

/**
 * Action: Salvar formulário (2026-09-28). Lead do contexto, por variável,
 * escolhido na lista — ou nenhum (`linkToLead: false`). Por isso
 * `optionalContext` (molde do Atualizar lead).
 */
const ACTION_SAVE_FORM_RESPONSE_SCHEMA: NodeTypeSchema = {
  type: 'action_save_form_response', category: 'actions', label: 'Salvar formulário',
  description: 'Grava valores do fluxo como uma resposta de formulário (ligada ao lead ou não)',
  input: { optionalContext: { lead: true } },
  output: { providesContext: noProvides },
};

const ACTION_NFE_PDF_SCHEMA: NodeTypeSchema = {
  type: 'action_nfe_pdf', category: 'actions', label: 'Gerar PDF da nota fiscal',
  description: 'Monta o PDF da nota fiscal (DANFE NFC-e) a partir do XML da venda, hospeda o arquivo e grava o link em uma variável',
  input: {},
  output: { providesContext: noProvides },
};

const CONTROL_WAIT_FOR_SCHEMA: NodeTypeSchema = {
  type: 'control_wait_for', category: 'controls', label: 'Aguardar',
  description: 'Espera um tempo, um horário ou o horário comercial',
  input: { requiresContext: {} },
  output: { providesContext: noProvides },
};

const CONTROL_SPLIT_SCHEMA: NodeTypeSchema = {
  type: 'control_split', category: 'controls', label: 'Dividir Fluxo (A/B)',
  description: 'Distribui execuções aleatoriamente entre caminhos',
  input: { requiresContext: {} },
  output: { providesContext: noProvides },
};

export const NODE_TYPE_SCHEMAS: Record<WorkflowNodeType, NodeTypeSchema> = {
  // Triggers
  trigger_webhook: TRIGGER_WEBHOOK_SCHEMA,
  trigger_schedule: TRIGGER_SCHEDULE_SCHEMA,
  trigger_event: TRIGGER_EVENT_MESSAGE_RECEIVED_SCHEMA, // Default para evento
  trigger_manual: TRIGGER_MANUAL_SCHEMA,
  trigger_date_field: TRIGGER_DATE_FIELD_SCHEMA,
  trigger_inactivity: TRIGGER_INACTIVITY_SCHEMA,
  trigger_instagram_comment: TRIGGER_INSTAGRAM_COMMENT_SCHEMA,
  trigger_instagram_mention: TRIGGER_INSTAGRAM_MENTION_SCHEMA,

  // Actions
  action_send_message: ACTION_SEND_MESSAGE_SCHEMA,
  action_send_email: ACTION_SEND_EMAIL_SCHEMA,
  action_send_template: ACTION_SEND_TEMPLATE_SCHEMA,
  action_send_media: ACTION_SEND_MEDIA_SCHEMA,
  action_http_request: ACTION_HTTP_REQUEST_SCHEMA,
  action_query_database: ACTION_QUERY_DATABASE_SCHEMA,
  action_create_lead: ACTION_CREATE_LEAD_SCHEMA,
  action_update_lead: ACTION_UPDATE_LEAD_SCHEMA,
  action_update_contact: ACTION_UPDATE_CONTACT_SCHEMA,
  action_add_tag: ACTION_ADD_TAG_SCHEMA,
  action_remove_tag: ACTION_REMOVE_TAG_SCHEMA,
  action_assign: ACTION_ASSIGN_SCHEMA,
  action_set_variable: ACTION_SET_VARIABLE_SCHEMA,
  action_create_conversation: ACTION_CREATE_CONVERSATION_SCHEMA,
  action_transfer_conversation: ACTION_TRANSFER_CONVERSATION_SCHEMA,
  action_satisfaction_survey: ACTION_SATISFACTION_SURVEY_SCHEMA,
  action_ask_question: ACTION_ASK_QUESTION_SCHEMA,
  action_ask_form: ACTION_ASK_FORM_SCHEMA,
  action_next_number: ACTION_NEXT_NUMBER_SCHEMA,
  action_save_form_response: ACTION_SAVE_FORM_RESPONSE_SCHEMA,
  action_create_ticket: ACTION_CREATE_TICKET_SCHEMA,
  action_internal_notification: ACTION_INTERNAL_NOTIFICATION_SCHEMA,
  action_find_leads: ACTION_FIND_LEADS_SCHEMA,
  action_create_database_document: ACTION_CREATE_DATABASE_DOCUMENT_SCHEMA,
  action_create_checklist: ACTION_CREATE_CHECKLIST_SCHEMA,
  action_find_unit: ACTION_FIND_UNIT_SCHEMA,
  action_find_user: ACTION_FIND_USER_SCHEMA,
  action_find_contact: ACTION_FIND_CONTACT_SCHEMA,
  action_url_to_pdf: ACTION_URL_TO_PDF_SCHEMA,
  action_nfe_pdf: ACTION_NFE_PDF_SCHEMA,
  action_voice_clone: ACTION_VOICE_CLONE_SCHEMA,
  action_voice_tts: ACTION_VOICE_TTS_SCHEMA,
  action_voice_clone_delete: ACTION_VOICE_CLONE_DELETE_SCHEMA,
  action_mirror_media: ACTION_MIRROR_MEDIA_SCHEMA,

  // Controls
  control_if: CONTROL_IF_SCHEMA,
  control_switch: CONTROL_SWITCH_SCHEMA,
  control_wait_for: CONTROL_WAIT_FOR_SCHEMA,
  control_loop: CONTROL_LOOP_SCHEMA,
  control_split: CONTROL_SPLIT_SCHEMA,
  control_retry_scope: CONTROL_RETRY_SCOPE_SCHEMA,

  // AI
  ai_agent: AI_AGENT_SCHEMA,
  ai_agent_inline: AI_AGENT_INLINE_SCHEMA,

  // Skills
  skill_input: SKILL_INPUT_SCHEMA,
  skill_output: SKILL_OUTPUT_SCHEMA
};

/**
 * Schemas específicos para eventos (trigger_event tem variantes)
 */
export const EVENT_TRIGGER_SCHEMAS: Record<string, NodeTypeSchema> = {
  'message.received': TRIGGER_EVENT_MESSAGE_RECEIVED_SCHEMA,
  'conversation.created': TRIGGER_EVENT_CONVERSATION_CREATED_SCHEMA,
  'ticket.created': TRIGGER_EVENT_TICKET_CREATED_SCHEMA,
  'lead.created': TRIGGER_EVENT_LEAD_CREATED_SCHEMA,
  'contact.created': TRIGGER_EVENT_CONTACT_CREATED_SCHEMA
};

/**
 * Deriva o schema de contexto de um trigger_event pelo prefixo do eventType.
 * Cobre todos os eventos da UI — o mapa EVENT_TRIGGER_SCHEMAS tem schemas ricos
 * para 5 eventos; os demais recebem um schema derivado com o contexto correto
 * pela família do evento. Eventos de database, form e custom não fornecem
 * contexto de entidade (o backend não injeta entidades para esses gatilhos).
 */
/**
 * Legenda humana da opção "contato do contexto" adaptada ao evento do trigger
 * (ex: lead.won → "Contato do lead ganho"). Usada no seletor de destinatário do
 * envio de mensagem para deixar claro DE ONDE vem o contato.
 */
export function eventContextContactLabel(eventType: string): string | undefined {
  const exact: Record<string, string> = {
    'lead.won': 'Contato do lead ganho',
    'lead.lost': 'Contato do lead perdido',
    'lead.created': 'Contato do novo lead',
    'lead.confirmed': 'Contato do lead confirmado',
    'lead.stage_changed': 'Contato do lead',
    'ticket.resolved': 'Contato do ticket resolvido',
    'ticket.created': 'Contato do novo ticket',
    'contact.created': 'Contato do novo contato',
  };
  if (exact[eventType]) return exact[eventType];
  if (eventType.startsWith('lead.')) return 'Contato do lead';
  if (eventType.startsWith('ticket.')) return 'Contato do ticket';
  if (eventType.startsWith('contact.')) return 'Contato do contato';
  if (eventType.startsWith('conversation.') || eventType.startsWith('message.')) return 'Contato da conversa';
  return undefined;
}

export function deriveEventTriggerSchema(eventType: string): NodeTypeSchema {
  const contextContactLabel = eventContextContactLabel(eventType);
  const rich = EVENT_TRIGGER_SCHEMAS[eventType];
  if (rich) {
    return contextContactLabel
      ? { ...rich, output: { ...rich.output, providesContext: { ...rich.output.providesContext, contextContactLabel } } }
      : rich;
  }

  // Entidade vinda da fonte única (event-entities.ts). Conversa, lead, contato
  // e ticket trazem as entidades RELACIONADAS (`relatedContextFor`) — o
  // enrichment do backend carrega todas.
  // Evento de agenda declara só `event`: o contato só existe quando o
  // compromisso foi marcado com alguém, e prometer `contact` num evento sem
  // contato faria o nó seguinte oferecer "contato do contexto" e falhar.
  const entidade = entityForEventType(eventType);
  const provides: Partial<AvailableContext> =
    entidade === 'lead' || entidade === 'ticket' || entidade === 'contact' || entidade === 'conversation'
      ? relatedContextFor(entidade)
      : entidade === 'event' ? { event: true }
      : {}; // database.*, form.*, custom.*, calendar_event.cancelled — sem entidade

  return {
    type: 'trigger_event',
    category: 'triggers',
    label: 'Evento',
    description: `Evento ${eventType}`,
    input: { requiresContext: {} },
    output: {
      providesContext: {
        conversation: provides.conversation ?? false,
        contact: provides.contact ?? false,
        lead: provides.lead ?? false,
        ticket: provides.ticket ?? false,
        ...(provides.event === true ? { event: true } : {}),
        ...(contextContactLabel ? { contextContactLabel } : {}),
      },
    },
  };
}

// ============================================================
// UTILIDADES PARA CÁLCULO DE CONTEXTO
// ============================================================

/**
 * Contexto inicial vazio
 */
export const EMPTY_CONTEXT: AvailableContext = {
  conversation: false,
  contact: false,
  lead: false,
  ticket: false,
  ai: false
};

/**
 * Calcula o contexto disponível após um node ser executado
 * Merge do contexto atual com o que o node fornece
 */
/**
 * Entidades RELACIONADAS que o motor entrega junto com a do gatilho
 * (`WorkflowRunner.attachRelatedEntities`, 23/09/2026). O contato é a ponte:
 * vem pelo `contactId` da entidade, e com ele a conversa ABERTA do contato
 * (ticket: a de onde nasceu, se aberta) e o lead do contato. Em runtime cada
 * uma pode faltar — contato sem conversa aberta, sem lead —, e o nó que
 * precisa dela decide (saída Falha ou erro da execução), como já era com o
 * lead do contato.
 */
export function relatedContextFor(entity: 'conversation' | 'lead' | 'contact' | 'ticket'): Partial<AvailableContext> {
  return {
    conversation: true,
    contact: true,
    lead: true,
    ...(entity === 'ticket' ? { ticket: true } : {}),
  };
}

/** `relatedContextFor` com as quatro chaves de entidade explícitas (schemas estáticos). */
function triggerProvidesContext(entity: 'conversation' | 'lead' | 'contact' | 'ticket'): AvailableContext {
  const related = relatedContextFor(entity);
  return {
    conversation: related.conversation ?? false,
    contact: related.contact ?? false,
    lead: related.lead ?? false,
    ticket: related.ticket ?? false,
  };
}

/**
 * Mapeamento entityType → contexto provido.
 * Quando um trigger baseado em entityType (inactivity, date_field) seleciona
 * um tipo de entidade, o contexto dinâmico é resolvido aqui.
 * O Entity Enrichment no backend garante que esses dados estarão disponíveis em runtime.
 */
export const ENTITY_TYPE_CONTEXT: Record<string, Partial<AvailableContext>> = {
  conversation: relatedContextFor('conversation'),
  lead:         relatedContextFor('lead'),
  contact:      relatedContextFor('contact'),
  ticket:       relatedContextFor('ticket'),
  // Calendar events carry contactId — enrichment provides the contact e o
  // próprio evento (regras de cancelamento de calendário precisam de `event`)
  event:        { contact: true, event: true },
};

export function calculateContextAfterNode(
  currentContext: AvailableContext,
  nodeType: WorkflowNodeType,
  config?: { eventType?: string; entityType?: string },
): AvailableContext {
  // Para trigger_event, usar schema específico do evento
  let schema: NodeTypeSchema;

  if (nodeType === 'trigger_event' && config?.eventType) {
    schema = deriveEventTriggerSchema(config.eventType);
  } else {
    schema = NODE_TYPE_SCHEMAS[nodeType];
  }

  if (!schema) {
    return currentContext;
  }

  // Para triggers baseados em entityType (inactivity, date_field),
  // o contexto é dinâmico: depende do entityType selecionado pelo usuário.
  const isEntityBasedTrigger = nodeType === 'trigger_inactivity' || nodeType === 'trigger_date_field';
  if (isEntityBasedTrigger && config?.entityType) {
    const entityContext = ENTITY_TYPE_CONTEXT[config.entityType] ?? {};
    const entityLabel: Record<string, string> = {
      lead: 'Contato do lead', ticket: 'Contato do ticket',
      conversation: 'Contato da conversa', contact: 'Contato do contato',
      event: 'Contato do evento',
    };
    return {
      conversation: currentContext.conversation || entityContext.conversation || false,
      contact: currentContext.contact || entityContext.contact || false,
      lead: currentContext.lead || entityContext.lead || false,
      ticket: currentContext.ticket || entityContext.ticket || false,
      ai: currentContext.ai || false,
      ...(currentContext.event || entityContext.event ? { event: true } : {}),
      ...(currentContext.instagramAuthor ? { instagramAuthor: true } : {}),
      ...(currentContext.contextContactLabel || entityLabel[config.entityType]
        ? { contextContactLabel: currentContext.contextContactLabel ?? entityLabel[config.entityType] }
        : {}),
    };
  }

  // Merge: contexto atual + o que o node fornece (schema estático)
  const provides = schema.output.providesContext;
  return {
    conversation: currentContext.conversation || provides.conversation,
    contact: currentContext.contact || provides.contact,
    lead: currentContext.lead || provides.lead,
    ticket: currentContext.ticket || provides.ticket,
    ai: currentContext.ai || provides.ai || false,
    ...(currentContext.event || provides.event ? { event: true } : {}),
    ...(currentContext.instagramAuthor || provides.instagramAuthor ? { instagramAuthor: true } : {}),
    ...(provides.contextContactLabel || currentContext.contextContactLabel
      ? { contextContactLabel: provides.contextContactLabel ?? currentContext.contextContactLabel }
      : {}),
  };
}

/**
 * Calcula o contexto acumulado em um ponto do workflow
 * Percorre todos os nodes anteriores e acumula o contexto
 */
export function calculateAvailableContext(
  nodes: Array<{ id: string; type: WorkflowNodeType; data?: { type?: WorkflowNodeType; config?: Record<string, unknown> } }>,
  edges: Array<{ source: string; target: string }>,
  targetNodeId: string
): AvailableContext {
  // Encontrar todos os nodes que levam ao target
  const predecessors = findAllPredecessors(nodes, edges, targetNodeId);

  // Nos nós crus do React Flow, `node.type` é o RENDERIZADOR
  // ('triggerNode'/'actionNode'/...) e o tipo real do workflow
  // ('trigger_event'/'action_send_message'/...) vive em `node.data.type`.
  // Sem esse fallback, NODE_TYPE_SCHEMAS[renderizador] = undefined e todo
  // predecessor contribui contexto vazio (some "Contato do contexto",
  // some o autocomplete de variáveis). Mesmo idiom de convertNodes/findExecutionOrder.
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const nodeTypeOf = (nodeId: string): WorkflowNodeType | undefined => {
    const node = nodeById.get(nodeId);
    return node ? ((node.data?.type || node.type) as WorkflowNodeType) : undefined;
  };

  // Acumula o contexto de uma sequência de nós (em ordem topológica).
  const fold = (nodeIds: string[]): AvailableContext => {
    let context = { ...EMPTY_CONTEXT };
    for (const nodeId of nodeIds) {
      const nodeType = nodeTypeOf(nodeId);
      if (nodeType) {
        const config = nodeById.get(nodeId)?.data?.config as { eventType?: string; entityType?: string } | undefined;
        context = calculateContextAfterNode(context, nodeType, config);
      }
    }
    return context;
  };

  // Vários gatilhos chegando ao nó (23/09/2026): cada disparo roda só o
  // caminho do SEU gatilho, então o nó recebe o que TODOS os caminhos
  // garantem — a interseção, nunca a soma ("lead atualizado" + "webhook" não
  // oferece o lead, porque no disparo do webhook ele não existe).
  const triggers = predecessors.filter((nodeId) => nodeTypeOf(nodeId)?.startsWith('trigger_') === true);
  if (triggers.length <= 1) {
    return fold(predecessors);
  }

  const contexts = triggers.map((triggerId) => {
    const alcancaveis = findAllDescendants(edges, triggerId);
    return fold(predecessors.filter((nodeId) => nodeId === triggerId || alcancaveis.has(nodeId)));
  });
  return intersectContexts(contexts);
}

/** Nós alcançáveis a partir de um nó (sem ele). */
function findAllDescendants(edges: Array<{ source: string; target: string }>, fromNodeId: string): Set<string> {
  const found = new Set<string>();
  const pending = [fromNodeId];
  while (pending.length > 0) {
    const current = pending.pop() as string;
    for (const edge of edges) {
      if (edge.source === current && !found.has(edge.target)) {
        found.add(edge.target);
        pending.push(edge.target);
      }
    }
  }
  return found;
}

/**
 * O que TODOS os contextos garantem. A legenda do contato fica só quando é a
 * mesma em todos — senão a tela volta ao genérico "Contato do contexto".
 */
function intersectContexts(contexts: AvailableContext[]): AvailableContext {
  const all = (pick: (c: AvailableContext) => boolean | undefined): boolean => contexts.every((c) => pick(c) === true);
  const labels = new Set(contexts.map((c) => c.contextContactLabel));
  const label = labels.size === 1 ? contexts[0]?.contextContactLabel : undefined;
  return {
    conversation: all((c) => c.conversation),
    contact: all((c) => c.contact),
    lead: all((c) => c.lead),
    ticket: all((c) => c.ticket),
    ai: all((c) => c.ai),
    ...(all((c) => c.event) ? { event: true } : {}),
    ...(all((c) => c.instagramAuthor) ? { instagramAuthor: true } : {}),
    ...(label !== undefined ? { contextContactLabel: label } : {}),
  };
}

/**
 * Encontra todos os predecessores de um node (ordem topológica)
 */
function findAllPredecessors(
  _nodes: Array<{ id: string; type: WorkflowNodeType }>,
  edges: Array<{ source: string; target: string }>,
  targetNodeId: string
): string[] {
  const visited = new Set<string>();
  const result: string[] = [];

  function dfs(nodeId: string): void {
    if (visited.has(nodeId)) return;

    // Encontrar edges que chegam neste node
    const incomingEdges = edges.filter(e => e.target === nodeId);

    // Primeiro visitar todos os predecessores
    for (const edge of incomingEdges) {
      dfs(edge.source);
    }

    // Depois adicionar este node
    visited.add(nodeId);
    result.push(nodeId);
  }

  // Começar do target
  const incomingEdges = edges.filter(e => e.target === targetNodeId);
  for (const edge of incomingEdges) {
    dfs(edge.source);
  }

  return result;
}

/**
 * Filtra opções condicionais baseado no contexto disponível
 * Retorna apenas as opções que têm contexto suficiente
 */
export function getAvailableOptions(
  nodeType: WorkflowNodeType,
  availableContext: AvailableContext
): ConditionalOption[] {
  const schema = NODE_TYPE_SCHEMAS[nodeType];

  if (!schema?.conditionalOptions) {
    return [];
  }

  return schema.conditionalOptions.filter(option => {
    // Verificar se o contexto disponível satisfaz os requisitos
    const requirements = option.requiresContext;

    for (const [key, required] of Object.entries(requirements)) {
      if (required && !availableContext[key as keyof AvailableContext]) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Valida se um node pode ser colocado em uma posição do workflow
 * Baseado nos requisitos de contexto do node
 */
export function validateNodePlacement(
  nodeType: WorkflowNodeType,
  availableContext: AvailableContext
): { isValid: boolean; missingContext: string[] } {
  const schema = NODE_TYPE_SCHEMAS[nodeType];

  if (!schema) {
    return { isValid: true, missingContext: [] };
  }

  const requirements = schema.input.requiresContext;

  if (!requirements) {
    return { isValid: true, missingContext: [] };
  }

  const missingContext: string[] = [];

  for (const [key, required] of Object.entries(requirements)) {
    if (required && !availableContext[key as keyof AvailableContext]) {
      missingContext.push(key);
    }
  }

  return {
    isValid: missingContext.length === 0,
    missingContext
  };
}
